import React, { useEffect, useMemo, useState } from 'react';

import {
  Center,
  Flex,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import api from 'api';
import { Button, Icon } from 'components';
import Accordion from 'containers/Accordion';
import HistorySidebar from 'containers/History/Sidebar';
import Page from 'containers/Page';
import RouteForm from 'containers/Route/Form';
import Locations from 'containers/Route/Locations';
import Meta from 'containers/Route/Meta';
import SectionContent from 'containers/Section/Content';
import ShowInfo from 'containers/Show/Info';
import { ContentRoutes } from 'enums/routes';
import { AccessTypes, ActivityTypes, MethodTypes } from 'enums/types';
import { policy } from 'polices';
import useRouteStore from 'stores/useRoute';
import useUser from 'stores/useUser';

export default function Routes() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newActivity, setNewActivity] = useState(false);
  const [editing, setEditing] = useState(false);
  const { id } = useParams<GeneralRoute>();
  const [executionId, setExecutionId] = useState<number>(null);
  const toast = useToast();
  const { user } = useUser();
  const policies = policy(ContentRoutes.Routes);
  const { push } = useHistory();
  const [markerFocus, setMarkerFocus] = useState(0);
  const {
    route,
    setRoute,
    setQuery,
    setActivities,
    activities,
    setHistory,
    setLastActivity,
  } = useRouteStore();

  useEffect(() => {
    if (policies(MethodTypes.View) === AccessTypes.Disallow) {
      push('/403');
    }
  }, []);
  const isEditable = useMemo(() => policies(MethodTypes.Update) !== AccessTypes.Disallow, []);

  const { refetch, isFetching } = useQuery([ContentRoutes.Routes, id], () => api().get(`/${ContentRoutes.Routes}/${id}`), {
    cacheTime: 0,
    onSuccess: ({ data }) => {
      setRoute(data);
      if (!executionId) {
        setHistory(data.history);
        setActivities(data.activities);
        const lastActivity = data.activities[data.activities.length - 1];
        if (lastActivity) {
          setLastActivity({
            drivers: lastActivity?.drivers,
            vehicle: lastActivity?.vehicle,
          });
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Ocorreu um erro.',
        description: error.message,
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
    },
  });
  const { refetch: refetchActivities, isFetching: isFetchingActivities } = useQuery(['execution', id, executionId], () => api().get(`/${ContentRoutes.Routes}/${id}/${executionId}`), {
    cacheTime: 0,
    enabled: !!executionId,
    onSuccess: ({ data }) => {
      setHistory(data.history);
      setActivities(data.activities);
      const lastActivity = data.activities[data.activities.length - 1];

      if (lastActivity?.type === ActivityTypes.Transhipment) {
        setLastActivity({
          drivers: lastActivity?.transhipmentDrivers,
          vehicle: lastActivity?.transhipmentVehicle,
        });
      }
      onOpen();
    },
    onError: (error: any) => {
      toast({
        title: 'Ocorreu um erro.',
        description: error.message,
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    setQuery({
      refetch: () => {
        refetch();
        if (executionId) {
          refetchActivities();
        }
      },
      isLoading: isFetching,
    });
  }, []);
  const executions = useMemo(() => [{
    label: 'Geral',
    id: null,
    onClick: () => setExecutionId(null),
  }, ...(route?.executions?.map((
    (execution) => ({
      label: dayjs(execution.startDate).format('DD/MM/YYYY').concat(` (iniciada em: ${dayjs(execution.startedAt).format('DD/MM/YYYY')})`),
      id: execution.id,
      onClick: () => setExecutionId(execution.id),
    })
  )) || [])],
  [route]);
  return (
    <Page
      isLoading={isFetching || isFetchingActivities}
      p={0}>
      <Center
        cursor="pointer"
        onClick={onOpen}
        borderRadius={4}
        bg="white.default"
        p={3}
        shadow={2}
        position="fixed"
        top={20}
        right={8}>
        <Icon
          color="gray.500"
          name="calendar-check"
        />
      </Center>
      <Flex
        maxW="1024px"
        mx="auto"
        w="100%"
        pr={5}
        py={6}
        pl={10}
        flexDirection="column">
        <Meta
          executionId={executionId}
          executions={executions}
          isEditable={isEditable}
          onEdit={() => setEditing(true)}
        />
        {(editing && isEditable) && (
        <RouteForm
          mb={10}
          defaultValues={route}
          onCancel={() => {
            refetch();
            setEditing(false);
          }}
        />
        )}
        {(!editing && policies(MethodTypes.View) !== AccessTypes.Disallow && user?.role?.name !== 'Parceiro') && (
          <ShowInfo
            data={route}
            fallbackContent={ContentRoutes.Routes}
          />
        )}
        {executionId && (
        <Flex my={6}>
          <Locations
            markerFocus={markerFocus}
            markers={activities?.map(
              (activity) => (
                {
                  ...activity,
                  latitude: activity.lastUpdateLatitude,
                  longitude: activity.lastUpdateLongitude,
                }
              ),
            )}
          />
        </Flex>
        )}
        <Stack my={6}>
          {activities?.map((activity, index) => (
            (
              <Accordion
                onClick={() => setMarkerFocus(index)}
                key={`activity-${index}-${activity.id}`}
                index={index}
                type={activity.type}
                title={activity.type}
                status={activity.status}
                activity={activity}>
                <SectionContent
                  isExecution={!!executionId}
                  data={activity}
                  onSuccess={() => {
                    refetch();
                    if (executionId) {
                      refetchActivities();
                    }
                  }}
                  index={index}
                />
              </Accordion>
            )))}
        </Stack>
        {newActivity && (
        <Accordion
          index={activities?.length ?? 0}>
          <SectionContent
            isNew
            onClose={() => setNewActivity(false)}
            onSuccess={() => {
              refetch();
              if (executionId) {
                refetchActivities();
              }
            }}
            index={activities?.length ?? 0}
          />
        </Accordion>
        )}
        {isEditable && (
        <Flex
          hidden={!!executionId}
          alignSelf="flex-end"
          my={4}>
          <Button
            onClick={() => setNewActivity(true)}
            variant="primary.blue"
            size="sm">
            <Text variant="smallBold">+ Adicionar nova Atividade</Text>
          </Button>
        </Flex>
        )}
      </Flex>
      <HistorySidebar
        isOpen={isOpen}
        onClose={onClose}
      />
    </Page>
  );
}
